import {
  Examination_Type,
  ExaminationHeaderQuery,
  ListTrialFilterInput,
  PlatformTrialListOrderFields,
  PlatformTrialListOrderInput,
  Trial_File_Type,
  TrialListQuery,
  useExaminationCompetencyListQuery,
  useGetReportFilesLazyQuery,
  useSendExaminationReminderNotificationMutation,
} from "@generated/graphql";
import { MailOutline } from "@mui/icons-material";
import { Button, Tooltip } from "@mui/material";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { downloadFile } from "helpers/fetch/downloadFile";
import { getTestingPath } from "helpers/index";
import { getExaminationFileName, getIsTrialEnableForReminder, getTrialFileName } from "helpers/trials.helper";
import JSZip from "jszip";
import TableConstructor from "layout/TableConstructor";
import { RouteEnum } from "layout/TableConstructor/types";
import qs from "qs";
import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TrialsListRefetch } from "../../types";
import columnConfig, { TrialItem } from "./tableConfig/columnConfig";
import filterConfig, { TrialFilter } from "./tableConfig/filterConfig";

interface TrialListProps {
  examination: ExaminationHeaderQuery["examination"];
  trialsData: TrialListQuery["trials"] | undefined;
  loading: boolean;
  permanentFilter: ListTrialFilterInput | undefined;
  defaultOrder: PlatformTrialListOrderInput;
  refetch: TrialsListRefetch;
}

const TrialList = ({ examination, trialsData, loading, permanentFilter, defaultOrder, refetch }: TrialListProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useContext(ToastContext);
  const [selectedTrials, setSelectedTrials] = useState<string[]>([]);

  const { data: competencyList, loading: competencylistLoading } = useExaminationCompetencyListQuery({
    variables: {
      examinationId: id!,
    },
    skip: !id,
  });

  const [getReportFiles, { loading: getReportFilesLoading }] = useGetReportFilesLazyQuery({
    variables: { input: { examinationId: id!, type: Trial_File_Type.RecruiterReport } },
  });

  const [sendExaminationReminderNotification, { loading: sendExaminationReminderNotificationLoading }] =
    useSendExaminationReminderNotificationMutation();

  const trials = trialsData?.data || [];
  const totalCount = trialsData?.totalCount || 0;
  const competencyListData = competencyList?.examination.competencyMap?.competences;
  const isGroupExamination = examination.type === Examination_Type.Group;

  const renderControls = () => {
    const handleClick = async () => {
      const zip = new JSZip();

      const { data } = await getReportFiles();
      const reportFiles = data?.getReportFiles;

      if (!reportFiles?.length) {
        return addToast({ text: "Отсутствуют отчеты для скачивания", type: ToastTypeEnum.ERROR });
      }

      await Promise.all(
        reportFiles.map(async (reportFile, index) => {
          const { file, trial } = reportFile;
          const response = await fetch(file.url);
          const blob = await response.blob();

          zip.file(
            `${index + 1}. ${getTrialFileName({
              trial,
              examination,
              competencyMap: trial.examination.competencyMap,
              companyCandidate: trial.companyCandidate,
            })}`,
            blob
          );
        })
      );

      const content = await zip.generateAsync({ type: "blob" });
      return downloadFile({ blob: content, filename: getExaminationFileName({ examination }) });
    };

    const disabled = getReportFilesLoading;

    return (
      <Tooltip title={!totalCount ? "Отсутствуют тестирования" : ""}>
        <span>
          <Button
            onClick={handleClick}
            disabled={disabled}
            variant='contained'>
            Выгрузить все отчеты
          </Button>
        </span>
      </Tooltip>
    );
  };

  const renderLeftControls = () => {
    const onError = (err: unknown) => {
      addToast({ text: "Ошибка отправки напоминаний", type: ToastTypeEnum.ERROR });

      // eslint-disable-next-line no-console
      console.log("err", err);
    };

    const handleClick = () => {
      sendExaminationReminderNotification({
        variables: { input: { id: examination.id, trialIds: selectedTrials } },
      })
        .then(({ data, errors }) => {
          if (data?.sendExaminationReminderNotification) {
            addToast({ text: "Напоминания отправлены кандидатам", type: ToastTypeEnum.SUCCESS });
            setSelectedTrials([]);
          } else {
            onError(errors);
          }
        })
        .catch(onError);
    };

    const disabled = !selectedTrials.length || !totalCount || sendExaminationReminderNotificationLoading;

    const getTooltip = () => {
      if (!totalCount) return "Отсутствуют тестирования";
      if (!selectedTrials.length) return "Не выбраны тестирования";
      return "";
    };

    return (
      <Tooltip title={getTooltip()}>
        <span>
          <Button
            onClick={handleClick}
            disabled={disabled}
            color='secondary'
            variant='contained'
            sx={{
              backgroundColor: "white",
              boxShadow: "0px 0px 7.3px 0px rgba(164, 184, 253, 0.24)",
              whiteSpace: "nowrap",
            }}
            startIcon={<MailOutline />}>
            Отправить напоминание
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <TableConstructor<TrialItem, TrialFilter, PlatformTrialListOrderFields, undefined, RouteEnum.Trial>
      exportRoute={RouteEnum.Trial}
      data={trials}
      totalCount={totalCount}
      loading={loading || competencylistLoading}
      onRowClick={(trial) =>
        navigate({ pathname: getTestingPath("trial", trial.id), search: qs.stringify({ examinationId: id }) })
      }
      columnsConfig={columnConfig({
        competencyList: competencyListData,
      })}
      defaultVisibleColumnList={[
        "candidateNumericId",
        "candidateFullName",
        "completedAt",
        "status",
        "action",
        "score",
        ...(competencyListData ? competencyListData.map((c) => `competency${c.order}` as `competency${number}`) : []),
      ]}
      filtersConfig={filterConfig(competencyListData)}
      emptyPlaceholderText='Пока нет тестирований'
      refetch={refetch}
      storageName='examinationTrialList'
      settingsModalTitle='Список тестирований'
      defaultOrder={defaultOrder}
      permanentFilter={permanentFilter}
      renderLeftControls={isGroupExamination ? renderLeftControls : undefined}
      renderControls={renderControls}
      selectedProps={isGroupExamination ? [selectedTrials, setSelectedTrials] : undefined}
      getCheckboxDisabled={(row) => !getIsTrialEnableForReminder(row)}
    />
  );
};

export default TrialList;
